import React, { useState, useEffect } from 'react';
import {
  X,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  BookOpen,
  Calendar,
  TrendingUp,
  AlertCircle,
  Check
} from 'lucide-react';
import adminEnrollmentsService, { type EnrollmentDetails } from '@/services/adminEnrollments.service';

interface EnrollmentManagerProps {
  userId: string;
  userName: string;
  onClose: () => void;
}

export default function EnrollmentManager({ userId, userName, onClose }: EnrollmentManagerProps) {
  const [enrollments, setEnrollments] = useState<EnrollmentDetails[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEnrollment, setEditingEnrollment] = useState<EnrollmentDetails | null>(null);
  const [editForm, setEditForm] = useState<{
    status: 'active' | 'expired' | 'refunded' | 'cancelled';
    progress_percentage: number;
  }>({
    status: 'active',
    progress_percentage: 0
  });
  
  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingIds, setDeletingIds] = useState<string[]>([]);

  useEffect(() => {
    fetchEnrollments();
  }, [userId]);

  const fetchEnrollments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminEnrollmentsService.searchEnrollments({ user_id: userId });
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch enrollments');
      }
      
      setEnrollments(response.data || []);
    } catch (err: any) {
      console.error('Error fetching enrollments:', err);
      setError(err.message || 'Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(enrollments.map(e => e.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleEditClick = (enrollment: EnrollmentDetails) => {
    setEditingEnrollment(enrollment);
    setEditForm({
      status: enrollment.status,
      progress_percentage: enrollment.progress_percentage
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingEnrollment) return;

    setProcessing(true);
    setError(null);
    try {
      const response = await adminEnrollmentsService.updateEnrollment(
        editingEnrollment.id,
        editForm
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to update enrollment');
      }

      setSuccess('Enrollment updated successfully!');
      setEditModalOpen(false);
      setEditingEnrollment(null);
      fetchEnrollments();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error updating enrollment:', err);
      setError(err.message || 'Failed to update enrollment');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteClick = (enrollmentIds: string[]) => {
    setDeletingIds(enrollmentIds);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setProcessing(true);
    setError(null);
    try {
      if (deletingIds.length === 1) {
        // Single delete
        const response = await adminEnrollmentsService.deleteEnrollment(deletingIds[0]);
        if (!response.success) {
          throw new Error(response.error || 'Failed to delete enrollment');
        }
        setSuccess('Enrollment deleted successfully!');
      } else {
        // Batch delete
        const response = await adminEnrollmentsService.batchDeleteEnrollments(deletingIds);
        if (!response.success) {
          throw new Error(response.error || 'Failed to delete enrollments');
        }
        setSuccess(`Successfully deleted ${response.data?.deleted_count || deletingIds.length} enrollment(s)!`);
      }

      setDeleteConfirmOpen(false);
      setDeletingIds([]);
      setSelectedIds(new Set());
      fetchEnrollments();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error deleting enrollment(s):', err);
      setError(err.message || 'Failed to delete enrollment(s)');
      setDeleteConfirmOpen(false);
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; icon: any }> = {
      active: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle2 },
      expired: { bg: 'bg-gray-500/20', text: 'text-gray-400', icon: XCircle },
      refunded: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: AlertCircle },
      cancelled: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full ${config.bg} ${config.text} text-xs font-medium`}>
        <Icon className="w-3 h-3" />
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  const canEdit = selectedIds.size === 1;
  const canDelete = selectedIds.size > 0;
  const allSelected = enrollments.length > 0 && selectedIds.size === enrollments.length;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col border border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">Manage Enrollments</h2>
            <p className="text-gray-400 text-sm">User: {userName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="mx-6 mt-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 flex items-center space-x-2">
            <Check className="w-5 h-5 flex-shrink-0" />
            <span>{success}</span>
            <button onClick={() => setSuccess(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="p-6 border-b border-white/10 flex items-center space-x-3">
          <button
            onClick={() => {
              const selectedId = Array.from(selectedIds)[0];
              const enrollment = enrollments.find(e => e.id === selectedId);
              if (enrollment) handleEditClick(enrollment);
            }}
            disabled={!canEdit}
            className={`px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-all ${
              canEdit
                ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                : 'bg-white/5 text-gray-600 cursor-not-allowed'
            }`}
            title={canEdit ? 'Edit selected enrollment' : 'Select exactly one enrollment to edit'}
          >
            <Edit2 className="w-4 h-4" />
            <span>Edit</span>
          </button>

          <button
            onClick={() => handleDeleteClick(Array.from(selectedIds))}
            disabled={!canDelete}
            className={`px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-all ${
              canDelete
                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                : 'bg-white/5 text-gray-600 cursor-not-allowed'
            }`}
            title={canDelete ? `Delete ${selectedIds.size} enrollment(s)` : 'Select enrollments to delete'}
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete ({selectedIds.size})</span>
          </button>

          {selectedIds.size > 0 && (
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-4 py-2 rounded-lg font-medium bg-white/10 text-gray-400 hover:bg-white/20 transition-all"
            >
              Clear Selection
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          ) : enrollments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <BookOpen className="w-16 h-16 mb-4 opacity-50" />
              <p>No enrollments found for this user</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="w-4 h-4 rounded border-white/10 bg-black/40 text-purple-600 focus:ring-purple-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Course</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Progress</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Enrolled</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {enrollments.map((enrollment) => (
                    <tr key={enrollment.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(enrollment.id)}
                          onChange={(e) => handleSelectOne(enrollment.id, e.target.checked)}
                          className="w-4 h-4 rounded border-white/10 bg-black/40 text-purple-600 focus:ring-purple-500"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-white font-medium">{enrollment.product_title}</div>
                        {enrollment.last_accessed_at && (
                          <div className="text-xs text-gray-400 mt-1 flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>Last: {formatDate(enrollment.last_accessed_at)}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-gray-400 text-sm capitalize">{enrollment.product_type}</span>
                      </td>
                      <td className="px-4 py-4">
                        {getStatusBadge(enrollment.status)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-purple-600 to-blue-600 transition-all"
                              style={{ width: `${enrollment.progress_percentage}%` }}
                            />
                          </div>
                          <span className="text-white text-sm font-medium">{enrollment.progress_percentage}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-white">{formatCurrency(enrollment.purchase_price)}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-gray-400 text-sm">{formatDate(enrollment.enrolled_at)}</span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditClick(enrollment)}
                            className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick([enrollment.id])}
                            className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editModalOpen && editingEnrollment && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-white/10">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-xl font-bold text-white">Edit Enrollment</h3>
              <p className="text-gray-400 text-sm mt-1">{editingEnrollment.product_title}</p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value as 'active' | 'expired' | 'refunded' | 'cancelled' })}
                  className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500/50 focus:outline-none"
                >
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="refunded">Refunded</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Progress ({editForm.progress_percentage}%)
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={editForm.progress_percentage}
                  onChange={(e) => setEditForm({ ...editForm, progress_percentage: parseInt(e.target.value) })}
                  className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #8b5cf6 0%, #3b82f6 ${editForm.progress_percentage}%, rgba(255,255,255,0.1) ${editForm.progress_percentage}%, rgba(255,255,255,0.1) 100%)`
                  }}
                />
              </div>
            </div>

            <div className="p-6 border-t border-white/10 flex space-x-3">
              <button
                onClick={handleSaveEdit}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50"
              >
                {processing ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => {
                  setEditModalOpen(false);
                  setEditingEnrollment(null);
                }}
                disabled={processing}
                className="px-6 py-3 bg-white/10 rounded-lg text-gray-400 hover:bg-white/20 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-red-500/30">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-xl font-bold text-white">Confirm Delete</h3>
            </div>

            <div className="p-6">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                <p className="text-red-400">
                  Are you sure you want to delete {deletingIds.length} enrollment(s)?
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-white/10 flex space-x-3">
              <button
                onClick={handleConfirmDelete}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-800 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50"
              >
                {processing ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setDeletingIds([]);
                }}
                disabled={processing}
                className="px-6 py-3 bg-white/10 rounded-lg text-gray-400 hover:bg-white/20 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
